import React, { useState, useEffect } from 'react';
import { Box, IconButton, CircularProgress, Button } from '@mui/material';
import { 
  Check as CheckIcon, 
  PlayArrow as PlayIcon, 
  Pause as PauseIcon,
  CloudDownload as CloudDownloadIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { loadVoiceMemoBlob, setCurrentlyPlaying } from 'lib/redux/linkedin/voice-memos/slice';
import { AppDispatch } from 'store';

interface VoiceMemo {
  id: string;
  name: string;
  blob?: Blob;
  duration: number;
  url: string;
}

interface VoiceMemoListProps {
  voiceMemos: VoiceMemo[];
  onSelect: (memo: VoiceMemo) => void;
  loadingMemoId?: string | null;
}

export const VoiceMemoList: React.FC<VoiceMemoListProps> = ({ 
  voiceMemos, 
  onSelect,
  loadingMemoId 
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [playingMemoId, setPlayingMemoId] = useState<string | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [actualDurations, setActualDurations] = useState<Record<string, number>>({});
  
  // Get loading states and blobs from Redux
  const loadingStates = useSelector((state: RootState) => state.linkedinVoiceNotes.loadingStates);
  const voiceNotesInRedux = useSelector((state: RootState) => state.linkedinVoiceNotes.vms || []);
  const currentlyPlaying = useSelector((state: RootState) => state.linkedinVoiceNotes.currentlyPlaying);

  // Sync local playing state with Redux
  useEffect(() => {
    if (!currentlyPlaying && playingMemoId) {
      audioElement?.pause();
      setPlayingMemoId(null);
      setAudioElement(null);
    }
  }, [currentlyPlaying, playingMemoId, audioElement]);

  // Helper to check if a memo is loaded
  const isLoaded = (memo: VoiceMemo) => {
    if (memo.blob) return true;
    
    // Check if loaded in Redux store
    const reduxMemo = voiceNotesInRedux.find(vm => vm.linkedin_voice_memo_id === memo.id);
    return !!reduxMemo?.blob;
  };

  // Helper to get the blob for a memo (either local or from Redux)
  const getBlob = (memo: VoiceMemo): Blob | undefined => {
    if (memo.blob) return memo.blob;
    
    const reduxMemo = voiceNotesInRedux.find(vm => vm.linkedin_voice_memo_id === memo.id);
    return reduxMemo?.blob;
  };

  // Get accurate duration once blob is loaded
  useEffect(() => {
    voiceMemos.forEach(async (memo) => {
      if (isLoaded(memo) && !actualDurations[memo.id]) {
        const blob = getBlob(memo);
        if (!blob) return;
        
        try {
          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);
          
          audio.addEventListener('loadedmetadata', () => {
            if (isFinite(audio.duration)) {
              setActualDurations(prev => ({
                ...prev,
                [memo.id]: audio.duration
              }));
            }
            URL.revokeObjectURL(url);
          });
          
          audio.addEventListener('error', () => {
            URL.revokeObjectURL(url);
          });
        } catch (error) {
          console.error('Error getting duration:', error);
        }
      }
    });
  }, [voiceMemos, voiceNotesInRedux]);

  const handleLoadMemo = async (memo: VoiceMemo) => {
    try {
      // Use the Redux thunk to load the blob
      await dispatch(loadVoiceMemoBlob(memo.id)).unwrap();
      
      // After loading, immediately handle the select if that's what was requested
      const blob = getBlob(memo);
      if (blob) {
        // Show toast notification
        const loadSuccessToast = document.createElement('div');
        loadSuccessToast.textContent = 'Voice note loaded!';
        loadSuccessToast.style.position = 'fixed';
        loadSuccessToast.style.bottom = '20px';
        loadSuccessToast.style.left = '50%';
        loadSuccessToast.style.transform = 'translateX(-50%)';
        loadSuccessToast.style.padding = '8px 16px';
        loadSuccessToast.style.backgroundColor = 'rgba(22, 163, 74, 0.9)';
        loadSuccessToast.style.color = 'white';
        loadSuccessToast.style.borderRadius = '4px';
        loadSuccessToast.style.zIndex = '9999';
        loadSuccessToast.style.fontSize = '14px';
        document.body.appendChild(loadSuccessToast);
        
        setTimeout(() => {
          document.body.removeChild(loadSuccessToast);
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to load voice memo:', error);
    }
  };

  const handlePlayPause = async (memo: VoiceMemo) => {
    // If this memo is already playing, pause it
    if (playingMemoId === memo.id) {
      audioElement?.pause();
      setPlayingMemoId(null);
      setAudioElement(null);
      dispatch(setCurrentlyPlaying(null));
      return;
    }

    // If not loaded yet, load it first
    if (!isLoaded(memo)) {
      await handleLoadMemo(memo);
      return;
    }

    try {
      // Stop any current playback
      if (audioElement) {
        audioElement.pause();
        setAudioElement(null);
      }

      // Get the blob
      const blob = getBlob(memo);
      if (!blob) {
        throw new Error('Failed to get audio blob');
      }

      // Create and play the audio
      const audio = new Audio(URL.createObjectURL(blob));
      
      // Set up audio event listeners
      audio.onended = () => {
        setPlayingMemoId(null);
        setAudioElement(null);
        dispatch(setCurrentlyPlaying(null));
        URL.revokeObjectURL(audio.src);
      };
      
      audio.onloadedmetadata = () => {
        if (isFinite(audio.duration)) {
          setActualDurations(prev => ({...prev, [memo.id]: audio.duration}));
        }
      };
      
      await audio.play();
      setAudioElement(audio);
      setPlayingMemoId(memo.id);
      dispatch(setCurrentlyPlaying(memo.id));
      
    } catch (error) {
      console.error('Failed to play voice memo:', error);
    }
  };

  const handleSelect = async (memo: VoiceMemo) => {
    // If already loaded, select immediately
    if (isLoaded(memo)) {
      const blob = getBlob(memo);
      if (blob) {
        // Use accurate duration if available
        const duration = actualDurations[memo.id] || memo.duration;
        onSelect({ ...memo, blob, duration });
      }
      return;
    }

    // Otherwise load first, then select
    try {
      await handleLoadMemo(memo);
      
      // Now check if it's loaded
      const blob = getBlob(memo);
      if (blob) {
        // Use accurate duration if available
        const duration = actualDurations[memo.id] || memo.duration;
        onSelect({ ...memo, blob, duration });
      }
    } catch (error) {
      console.error('Failed to load and select voice memo:', error);
    }
  };

  const formatTime = (seconds: number) => {
    if (!isFinite(seconds) || seconds <= 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get duration for display
  const getDisplayDuration = (memo: VoiceMemo) => {
    return actualDurations[memo.id] !== undefined 
      ? actualDurations[memo.id] 
      : memo.duration;
  };

  if (voiceMemos.length === 0) {
    return null; // Return nothing since we'll handle the empty state in the parent component
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      margin: 0
    }}>
      {voiceMemos.map((memo) => {
        const isLoading = loadingMemoId === memo.id || loadingStates[memo.id];
        const hasBlob = isLoaded(memo);
        
        return (
          <Box
            key={memo.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 0.75,
              px: 1.5,
              borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
              "&:last-child": {
                borderBottom: 'none',
              },
              transition: 'all 0.15s ease',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            {/* Play Button */}
            <IconButton
              onClick={() => hasBlob ? handlePlayPause(memo) : handleLoadMemo(memo)}
              disabled={isLoading}
              size="small"
              sx={{
                width: 28,
                height: 28,
                minWidth: 28,
                mr: 1,
                color: playingMemoId === memo.id ? '#0a66c2' : 'rgba(0, 0, 0, 0.6)',
                p: 0,
                bgcolor: playingMemoId === memo.id ? 'rgba(10, 102, 194, 0.08)' : 'transparent',
                border: playingMemoId === memo.id ? '1px solid rgba(10, 102, 194, 0.2)' : 'none',
                '&:hover': {
                  bgcolor: 'rgba(10, 102, 194, 0.08)',
                  color: '#0a66c2'
                },
                '&.Mui-disabled': {
                  opacity: 0.5
                }
              }}
            >
              {isLoading ? (
                <CircularProgress size={14} thickness={4} sx={{ color: 'inherit' }} />
              ) : !hasBlob ? (
                <CloudDownloadIcon sx={{ fontSize: 14 }} />
              ) : playingMemoId === memo.id ? (
                <PauseIcon sx={{ fontSize: 16 }} />
              ) : (
                <PlayIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>

            {/* Name and Duration */}
            <Box 
              sx={{ 
                flex: 1,
                minWidth: 0,
                fontSize: '0.75rem',
                lineHeight: '1.3',
                cursor: 'pointer',
                mr: 1
              }}
              onClick={() => hasBlob ? handleSelect(memo) : handleLoadMemo(memo)}
            >
              <Box sx={{ 
                fontWeight: 500,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.8125rem',
                letterSpacing: '0.01em'
              }}>
                {memo.name}
              </Box>
              <Box sx={{ 
                fontSize: '0.7rem',
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}>
                {formatTime(getDisplayDuration(memo))}
              </Box>
            </Box>

            {/* Select Button - More Modern Styling */}
            {!hasBlob ? (
              <Button
                onClick={() => handleLoadMemo(memo)}
                disabled={isLoading}
                size="small"
                variant="text"
                startIcon={isLoading ? null : <CloudDownloadIcon sx={{ fontSize: 14 }} />}
                sx={{
                  minWidth: 0,
                  height: 24,
                  padding: '0px 8px',
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  color: 'primary.main',
                  borderRadius: 12,
                  '&:hover': {
                    backgroundColor: 'rgba(10, 102, 194, 0.04)'
                  },
                  '&.Mui-disabled': {
                    color: 'text.disabled'
                  }
                }}
              >
                {isLoading ? 
                  <CircularProgress size={12} thickness={4} sx={{ color: 'inherit' }} /> : 
                  "Load"
                }
              </Button>
            ) : (
              <Button
                onClick={() => handleSelect(memo)}
                size="small"
                variant="contained"
                disableElevation
                sx={{
                  minWidth: 0,
                  height: 24,
                  padding: '0px 10px',
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  color: 'white',
                  bgcolor: '#0a66c2',
                  borderRadius: 12,
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: '#004182'
                  }
                }}
              >
                Select
              </Button>
            )}
          </Box>
        );
      })}
    </Box>
  );
}; 